import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from "@angular/core";
import * as L from "leaflet";
import {globalBaseLayer} from "leaflet-map-layers";
import {AvdbIssueManifest} from "./avdb-issue-manifest";

@Component({
	selector: "subregion-map",
	template: `
		<div class="subregionmap_container">
			<div class="subregionmap_territories">
				<h3>{{manifest.territoryListTitle}}</h3>

				<ul class="subregionmap_territories_list">
					<li *ngFor="let territory of territories"
							[ngClass]="territorySelectionClass(territory)"
							(click)="toggleTerritorySelection(territory)">
						{{territoryDisplayName(territory)}}
					</li>
				</ul>

				<div class="subregionmap_selectcontrols">
					<button type="button" class="button-link" (click)="selectAll()">Select All</button>
					|
					<button type="button" class="button-link" (click)="selectNone()">Select None</button>
				</div>

				<ul class="subregionmap_territories_legend">
					<li>
						<span class="subregionmap_territory-selected">Green</span>
						– Fully Selected
					</li>
					<li>
						<span class="subregionmap_territory-partiallyselected">Orange</span>
						– Partially Selected
					</li>
				</ul>
			</div>

			<div class="subregionmap_mapcontainer">
				<div #map class="subregionmap_map" (elementResized)="onMapResize()"></div>
			</div>

			<div *ngIf="manifest.displayInfo.length > 0" class="subregionmap_legend">
				<h3>Legend</h3>

				<ul class="subregionmap_legend_list">
					<li *ngFor="let displayInfo of manifest.displayInfo">
						<span class="subregionmap_legend_color" [style.backgroundColor]="displayInfo.fillColor"></span>
						{{displayInfo.description}}
					</li>
				</ul>
			</div>
		</div>
	`,
})
export class SubregionMapComponent implements OnInit, AfterViewInit {
	@Input() manifest: AvdbIssueManifest;

	_selectedSubregionIDs = new Set<string>();

	@Input()
	set selectedSubregionIDs(value: Set<string>) {
		if (this._selectedSubregionIDs === value)
			return;
		this._selectedSubregionIDs = value || new Set<string>();
		this.updateSubregionPolygons();
	}

	@Output() selectedSubregionIDsChange = new EventEmitter<Set<string>>();

	subregionsByTerritory: Record<string, Array<string>>;
	territories: Array<string>;

	map: L.Map;
	@ViewChild("map") mapRef: ElementRef;
	displayInfoLayer = L.featureGroup();
	subregionsLayer = L.featureGroup();
	subregionPolygonsByID: Record<string, L.Polygon>;

	ngOnInit() {
		this.subregionsByTerritory = this.manifest.getSubregionsByTerritory();
		this.territories = Object.keys(this.subregionsByTerritory).sort();
	}

	ngAfterViewInit() {
		this.map = L.map(this.mapRef.nativeElement, {
			worldCopyJump: true,
		});

		for (let displayInfo of this.manifest.displayInfo) {
			let paths = displayInfo.paths.map(p => p.map(l => l.toTuple()));
			for (let path of paths) {
				let displayInfoPolygon = L.polygon(path, {
					interactive: false,
					stroke: null,
					fillOpacity: 1,
					fillColor: displayInfo.fillColor.trim(),
				});
				this.displayInfoLayer.addLayer(displayInfoPolygon);
			}
		}

		this.subregionPolygonsByID = {};
		for (let subregion of this.manifest.subregions) {
			let path = subregion.path.map(l => l.toTuple());
			let subregionPolygon = L.polygon(path, {
				className: "subregionmap_subregion",
				...subregionSelectionStyle(this._selectedSubregionIDs.has(subregion.id)),
			});
			subregionPolygon.on("click", () => this.toggleSubregionSelection(subregion.id));
			this.subregionPolygonsByID[subregion.id] = subregionPolygon;
			this.subregionsLayer.addLayer(subregionPolygon);
		}

		this.map.addLayer(globalBaseLayer);
		this.map.addLayer(this.displayInfoLayer);
		this.map.addLayer(this.subregionsLayer);

		this.map.fitBounds(this.subregionsLayer.getBounds());

		this.map.invalidateSize();
	}

	onMapResize() {
		this.map.invalidateSize();
	}

	ngOnDestroy() {
		this.map.remove();
	}

	territoryDisplayName(territory: string) {
		let map = territoryNameMap[this.manifest.territoryListTitle];
		if (map == null)
			return territory;
		return map[territory] || territory;
	}

	territorySelectionClass(territory: string) {
		let subregionIDs = this.subregionsByTerritory[territory];
		if (subregionIDs.every(s => this._selectedSubregionIDs.has(s)))
			return "subregionmap_territory-selected";
		else if (subregionIDs.some(s => this._selectedSubregionIDs.has(s)))
			return "subregionmap_territory-partiallyselected";
		return null;
	}

	toggleTerritorySelection(territory: string) {
		let subregionIDs = this.subregionsByTerritory[territory];
		let selected = !subregionIDs.every(s => this._selectedSubregionIDs.has(s));
		for (let subregionID of subregionIDs) {
			this.setSubregionSelection(subregionID, selected);
		}
		this.fireSelectionChange();
	}

	toggleSubregionSelection(subregionID: string) {
		this.setSubregionSelection(subregionID, !this._selectedSubregionIDs.has(subregionID));
		this.fireSelectionChange();
	}

	selectAll() {
		this._selectedSubregionIDs = new Set(this.manifest.subregions.map(s => s.id));
		this.updateSubregionPolygons();
		this.fireSelectionChange();
	}

	selectNone() {
		this._selectedSubregionIDs = new Set();
		this.updateSubregionPolygons();
		this.fireSelectionChange();
	}

	setSubregionSelection(subregionID: string, selected: boolean) {
		this._selectedSubregionIDs = new Set(this._selectedSubregionIDs);
		if (selected)
			this._selectedSubregionIDs.add(subregionID);
		else
			this._selectedSubregionIDs.delete(subregionID);

		this.updateSubregionPolygon(subregionID);
	}

	updateSubregionPolygons() {
		if (this.subregionPolygonsByID == null)
			return;

		for (let subregion of this.manifest.subregions) {
			this.updateSubregionPolygon(subregion.id);
		}
	}

	updateSubregionPolygon(subregionID: string) {
		let selected = this._selectedSubregionIDs.has(subregionID);
		let subregionPolygon = this.subregionPolygonsByID[subregionID];
		subregionPolygon.setStyle(subregionSelectionStyle(selected));
		subregionPolygon.redraw();
	}

	fireSelectionChange() {
		this.selectedSubregionIDsChange.next(this._selectedSubregionIDs);
	}
}

function subregionSelectionStyle(selected = false) {
	// It would be great if we could do this with CSS. Unfortunately, Leaflet doesn't offer a way to
	// update the class names on paths. https://github.com/Leaflet/Leaflet/issues/2662
	if (selected) {
		return {
			color: "rgba(0,0,0,0.5)",
			weight: 2,
			dashArray: null,
			fillOpacity: 1,
			fillColor: "rgba(0,255,0,0.3)",
		};
	}
	else {
		return {
			color: "rgba(0,0,0,0.5)",
			weight: 1,
			dashArray: "2 2",
			fillOpacity: 1,
			fillColor: "rgba(255,255,255,0.25)",
		};
	}
}


// For U.S. states only, the issue manifest uses abbreviations for territory names.
// TODO: We should fix the data so this isn't necessary
const territoryNameMap: Record<string, Record<string, string>> = {
	"US States": {
		AL: "Alabama",
		AK: "Alaska",
		AS: "American Samoa",
		AZ: "Arizona",
		AR: "Arkansas",
		CA: "California",
		CO: "Colorado",
		CT: "Connecticut",
		DE: "Delaware",
		DC: "District Of Columbia",
		FM: "Federated States Of Micronesia",
		FL: "Florida",
		GA: "Georgia",
		GU: "Guam",
		HI: "Hawaii",
		ID: "Idaho",
		IL: "Illinois",
		IN: "Indiana",
		IA: "Iowa",
		KS: "Kansas",
		KY: "Kentucky",
		LA: "Louisiana",
		ME: "Maine",
		MH: "Marshall Islands",
		MD: "Maryland",
		MA: "Massachusetts",
		MI: "Michigan",
		MN: "Minnesota",
		MS: "Mississippi",
		MO: "Missouri",
		MT: "Montana",
		NE: "Nebraska",
		NV: "Nevada",
		NH: "New Hampshire",
		NJ: "New Jersey",
		NM: "New Mexico",
		NY: "New York",
		NC: "North Carolina",
		ND: "North Dakota",
		MP: "Northern Mariana Islands",
		OH: "Ohio",
		OK: "Oklahoma",
		OR: "Oregon",
		PW: "Palau",
		PA: "Pennsylvania",
		PR: "Puerto Rico",
		RI: "Rhode Island",
		SC: "South Carolina",
		SD: "South Dakota",
		TN: "Tennessee",
		TX: "Texas",
		UT: "Utah",
		VT: "Vermont",
		VI: "Virgin Islands",
		VA: "Virginia",
		WA: "Washington",
		WV: "West Virginia",
		WI: "Wisconsin",
		WY: "Wyoming",
	},
};
